import { IObjectTranslatable, RelationalActionTranslator, ExternalCommonTranslator, TextLineConverter, TextAlignKindsTranslator } from ".";
import { IExternalDataItemBean, ITypeDefinable, BeanTypes } from "../beans/items";
import { IItem, ExternalDataItem } from "../../../../domain/document/items";
import { HtmlColorCodeTranslator } from "./HtmlColorCodeTranslator";
import { RelationalActionExtractor } from "./RelationalActionExtractor";

import {
  Point,
  Size,
  Rectangle,
  TextStyle,
  Color,
} from "../../../../domain/drawing";

export class ExternalDataItemTranslator implements IObjectTranslatable {
  toItem(item: ITypeDefinable, formatId: number, groupId: number, documentId: number, executionMode: string): IItem {
    const bean = item as IExternalDataItemBean;
    const result = new ExternalDataItem(TextLineConverter.convert(bean.text, !!bean.multiLine));
    result.privateId = (bean as any).privateId; // フロントエンド独自パラメータ.
    result.uniqueKey = bean.uniqueKey;
    result.id = bean.id;
    result.sectionObjectUid = bean.sectionObjectUid;
    result.isEditable = true; // TODO:特に問題なければプロパティを削除する
    result.isAdditional = !!bean.isAdditional;
    result.isDeleted = !!bean.isDeleted;
    result.isVisible = !!bean.isVisible;
    result.typeDetail = bean.typeDetail;
    result.viewTitle = bean.viewTitle;
    result.printBehavior.shouldPrint = !!bean.printVisible;
    result.printBehavior.shouldPreview = !!bean.previewVisible;
    result.name = bean.name;
    result.actions = RelationalActionTranslator.toItems(bean.relationalItems);
    result.shouldQuote = !!bean.isQuotable;
    result.supportedQuotable = !!bean.supportedQuotable;
    result.geometry.rectangle = new Rectangle(new Point(bean.x, bean.y), new Size(bean.width, bean.height));
    result.textStyle = new TextStyle(new Color(bean.textColor), TextAlignKindsTranslator.toKind(bean.textAlign), bean.textFontSize, bean.adjustedTextFontSize, bean.textFontName, bean.textLineHeight, !!bean.multiLine);
    result.imeMode = bean.imeMode;
    result.isFreeInputable = bean.supportedFreeText;
    result.shouldInputFree = bean.enabledFreeText;
    result.enableSingleEmptyCheck = bean.emptyCheck;
    result.enableEmptyCheckConfirmation = bean.isEmptyCheckIgnorable;
    result.enableNameGroupEmptyCheck = bean.nameGroupEmptyCheck;
    result.alertGroupKey = bean.alertGroup;
    result.toolTip = bean.toolTip;
    result.dataValidationKind = bean.dataValidation;
    result.exePath = bean.exePath;
    result.csvPath = bean.csvPath;
    result.xmlValuePath = bean.xmlValuePath;
    result.csvBeforeValuePath = bean.csvBeforeValuePath;
    result.csvInputValuePath = bean.csvInputValuePath;
    result.updateKind = bean.updateType;
    result.row = bean.row;
    result.column = bean.column;
    result.splitter = bean.splitter;
    result.items = ExternalCommonTranslator.toSubItems(bean.items);
    result.scriptExecuteParameters = RelationalActionExtractor.extractScriptExecuteParameters(result.actions);
    result.previousCsvBinary = bean.previousCsvBinary;
    result.enableLengthCheck = !!bean.lengthCheck;
    result.lengthChecklength = bean.length;
    result.pasteDestinations = RelationalActionExtractor.extractTextPasteParameters(result.actions);
    result.parentAlertInformations = RelationalActionExtractor.extractParentAlertParameters(result.actions);
    result.enabledAutoAdjustFontSize = !!bean.enabledAutoAdjustFontSize;
    result.autoNotePageSize = bean.autoNotePageSize;
    result.autoNotePageKind = bean.autoNotePageType;
    result.subName = bean.subName;
    result.paintStyle = HtmlColorCodeTranslator.toPaintStyleFill(bean.backColor);
    result.paintStyleOnEditing = bean.isDisplayBackColorSpecified
      ? HtmlColorCodeTranslator.toPaintStyleFill(bean.displayBackColor)
      : null;
    result.dacsProcessType = bean.dacsProcessType;
    result.dacsItemName = bean.dacsItemName;
    result.dacsItemCode = bean.dacsItemCode;
    result.isNaturalViewEnable = bean.naturalView;
    result.naturalText = bean.naturalText;
    return result;
  }

  toBean(item: IItem): IExternalDataItemBean {
    const target = item as ExternalDataItem;
    return {
      type: BeanTypes.ExternalData,
      uniqueKey: target.uniqueKey,
      id: target.id,
      sectionObjectUid: target.sectionObjectUid,
      name: target.name,
      isVisible: target.isVisible,
      printVisible: target.printBehavior.shouldPrint,
      previewVisible: target.printBehavior.shouldPreview,
      x: target.geometry.rectangle.x,
      y: target.geometry.rectangle.y,
      width: target.geometry.rectangle.width,
      height: target.geometry.rectangle.height,
      isAdditional: target.isAdditional,
      isDeleted: target.isDeleted,
      textColor: HtmlColorCodeTranslator.fromColor(target.textStyle.color),
      textAlign: TextAlignKindsTranslator.fromKind(target.textStyle.textAlign),
      backColor: HtmlColorCodeTranslator.fromPaintStyle(target.paintStyle),
      displayBackColor: HtmlColorCodeTranslator.fromPaintStyle(target.paintStyleOnEditing),
      textFontSize: target.textStyle.baseFontSize,
      textLineHeight: target.textStyle.lineHeight,
      textFontName: target.textStyle.fontFamily,
      imeMode: target.imeMode,
      supportedFreeText: target.isFreeInputable,
      enabledFreeText: target.shouldInputFree,
      text: target.value,
      relationalItems: null,  // TODO:返却する必要ある？
      emptyCheck: target.enableSingleEmptyCheck,
      isEmptyCheckIgnorable: target.enableEmptyCheckConfirmation,
      nameGroupEmptyCheck: target.enableNameGroupEmptyCheck,
      alertGroup: target.alertGroupKey,
      toolTip: target.toolTip,
      dataValidation: target.dataValidationKind,
      exePath: target.exePath,
      csvPath: target.csvPath,
      xmlValuePath: target.xmlValuePath,
      csvBeforeValuePath: target.csvBeforeValuePath,
      csvInputValuePath: target.csvInputValuePath,
      updateType: target.updateKind,
      row: target.row,
      column: target.column,
      splitter: target.splitter,
      items: null,  // TODO:返却する必要ある？
      previousCsvBinary: target.previousCsvBinary,
      lengthCheck: target.enableLengthCheck,
      length: target.lengthChecklength,
      enabledAutoAdjustFontSize: target.enabledAutoAdjustFontSize,
      autoNotePageSize: target.autoNotePageSize,
      autoNotePageType: target.autoNotePageKind,
      adjustedTextFontSize: target.textStyle.adjustedFontSize,
      multiLine: target.textStyle.isMultiLine,
      isQuotable: target.shouldQuote,
      supportedQuotable: target.supportedQuotable,
      subName: target.subName,
      isDisplayBackColorSpecified: !!target.paintStyleOnEditing,
      typeDetail: target.typeDetail,
      viewTitle: target.viewTitle,
      dacsProcessType: target.dacsProcessType,
      dacsItemName: target.dacsItemName,
      dacsItemCode: target.dacsItemCode,
      naturalView: target.isNaturalViewEnable,
      naturalText: target.naturalText,
    };
  }
}
